import { parseAnswer, parseInline } from './answer';

// Print or save as PDF (Gary, 1 Nov 2026): an AI team post laid out on a clean page, as the Portal's Ask
// saves an answer. Built from the same parser as the chat bubble; every piece of text is escaped, so a
// post can never become HTML. The browser's own print sheet offers "Save as PDF".

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function inline(text: string): string {
  return parseInline(text).map((p) => {
    if (p.kind === 'bold') return `<strong>${esc(p.text)}</strong>`;
    if (p.kind === 'italic') return `<em>${esc(p.text)}</em>`;
    if (p.kind === 'link') return `<a href="${esc(p.href)}">${esc(p.text)}</a>`;
    return esc(p.text);
  }).join('');
}

/** The post as a printable HTML page (exported for the test). */
export function postPage(title: string, meta: string, text: string): string {
  const body = parseAnswer(text).map((b) => {
    if (b.kind === 'h') return `<h2>${inline(b.text)}</h2>`;
    if (b.kind === 'ul' || b.kind === 'ol') {
      const items = b.items.map((it, k) => {
        const subs = b.subs[k] ?? [];
        return `<li>${inline(it)}${subs.length ? `<ul>${subs.map((s) => `<li>${inline(s)}</li>`).join('')}</ul>` : ''}</li>`;
      }).join('');
      return b.kind === 'ol' ? `<ol start="${b.start}">${items}</ol>` : `<ul>${items}</ul>`;
    }
    if (b.kind === 'table') {
      const head = `<tr>${b.table.headers.map((h) => `<th>${inline(h)}</th>`).join('')}</tr>`;
      const rows = b.table.rows.map((r) => `<tr>${r.map((c) => `<td>${inline(c)}</td>`).join('')}</tr>`).join('');
      return `<table>${head}${rows}</table>`;
    }
    return `<p>${b.lines.map(inline).join('<br>')}</p>`;
  }).join('');
  return `<!doctype html><html lang="en"><head><meta charset="utf-8"><title>${esc(title)}</title><style>
body{font-family:Inter,system-ui,sans-serif;color:#1d1a22;margin:32px;line-height:1.5;font-size:14px}
h1{font-size:20px;color:#772cbb;margin:0 0 4px}h2{font-size:16px;margin:18px 0 6px}.meta{color:#5d5866;margin-bottom:18px}
table{border-collapse:collapse;margin:8px 0}th,td{border:1px solid #dcdce4;padding:4px 8px;text-align:left}th{background:#f6f4f9}
</style></head><body><h1>${esc(title)}</h1><div class="meta">${esc(meta)}</div>${body}</body></html>`;
}

/** Opens the page and the print sheet. False when the browser blocked the new window. */
export function printPost(title: string, meta: string, text: string): boolean {
  const w = window.open('', '_blank');
  if (!w) return false;
  w.document.open();
  w.document.write(postPage(title, meta, text));
  w.document.close();
  w.focus();
  // A written page has no load event to wait for; a short pause lets it lay out first.
  setTimeout(() => { try { w.print(); } catch { /* closed already */ } }, 300);
  return true;
}
